import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { SuggestionApi } from 'api/suggestions';
import { preventDefault } from 'utils/mouseEvent';
import { useToast } from 'app/hooks/useToast';
import TextEditorConvertor from 'app/modules/textEditor/utils/textEditorConvertor';
import MentionSearchFailureNotice from 'app/modules/textEditor/components/MentionSearchFailureNotice';
import { Button, Field, TextInput } from '@kontentino/ui';
import type {
  MentionItem,
  MentionRefusalReason,
  MentionSearchEnvelope,
} from 'api/mentions/types';
import { SuggestionsList } from 'app/modules/textEditor/components/SuggestionsList';
import {
  HANDLE_AS_LABEL_PAGE_TYPES,
  PAGE_MENTION_PAGE_TYPES,
} from 'app/modules/textEditor/constants/mentionNetworks';

/**
 * The copy for each refusal. The server sends a code, not a sentence, so this
 * is translated like everything else the user reads.
 *
 * The LinkedIn one names the reason on purpose. A profile URL is a reasonable
 * thing to try — it works for companies, and nothing on screen says people are
 * different — so an honest "no match" would be truthful and useless: the user
 * retries, concludes mentions are flaky, and files the ticket this project
 * exists to stop.
 *
 * `instagram_unconfirmed` must never say the account is missing or wrong.
 * Instagram's `business_discovery` returns the identical error for a real
 * personal account as for an invented handle, so the only honest statement is
 * that we could not confirm it — and that the post will still publish, since
 * Instagram resolves `@handle` itself at that point, not us.
 *
 * It must not promise the mention will WORK either, and the earlier wording
 * did: "It will still publish — Instagram will recognise it" reads as a
 * guarantee, which holds for a real-but-unverifiable account and is simply
 * false for a typo. QA raised exactly that. The two cases cannot be told
 * apart — that is the whole reason this refusal exists — so the copy is
 * conditional instead: Instagram links the mention IF the account exists.
 *
 * The subject is now named rather than left to a pronoun. "It will still
 * publish" attached "it" to the nearest noun, the account, and most locales
 * translated it that way — telling the user their ACCOUNT would be published.
 * English is what translators copy, so the ambiguity had to be removed here
 * rather than patched downstream.
 */
const REFUSAL_COPY_KEY: Record<MentionRefusalReason, string> = {
  facebook_personal_profile: 'mentions.resolve.facebookPersonalProfile',
  linkedin_personal_profile: 'mentions.resolve.linkedInPersonalProfile',
  instagram_unconfirmed: 'mentions.resolve.instagramUnconfirmed',
};

type Props = {
  pageId: number;
  pageType: number;
  onCopyToText(link: string): void;
};

const DirectMention: FC<Props> = (props) => {
  const toast = useToast();
  const [value, setValue] = useState('');
  const [result, setResult] = useState<MentionSearchEnvelope | null>(null);
  /**
   * The candidates to choose between, when the lookup answered with more than
   * one.
   *
   * `resolve` used to return at most one candidate, so inserting `items[0]`
   * was the whole interaction. It no longer does: a plain typed name now falls
   * back to LinkedIn's company search (api#1060), and "Acme Group" legitimately
   * matches several real companies. Auto-inserting the first would silently
   * tag whichever one LinkedIn happened to rank highest, which is worse than
   * the dead end it replaced — it is wrong without looking wrong.
   */
  const [choices, setChoices] = useState<MentionItem[] | null>(null);
  const { t } = useTranslation();

  /**
   * Insert one chosen candidate and report the selection.
   *
   * Extracted from `onSuccess` because it now has two callers: the
   * unambiguous single-result path, and the chooser below.
   */
  function insertCandidate(item: MentionItem) {
    toast(t('mentions.mentionAdded'));

    const usesHandleAsLabel = HANDLE_AS_LABEL_PAGE_TYPES.includes(
      props.pageType,
    );
    // A handle that is missing or empty falls back to `name` rather
    // than inserting `@` or `@undefined` -- a mention that names the
    // wrong thing beats one that is malformed.
    const label = usesHandleAsLabel && item.handle ? item.handle : item.name;

    // WHAT to call it and HOW to insert it are two different questions,
    // and conflating them is what produced the duplicate-typeahead bug.
    //
    // The label comes from `HANDLE_AS_LABEL_PAGE_TYPES` (Instagram and
    // X publish `@handle`, not a display name). The insertion FORM comes
    // from whether this page type has a tiptap `mention` node at all,
    // which is `PAGE_MENTION_PAGE_TYPES` -- and X is in BOTH lists. An
    // earlier comment here asserted X had no node; it does, registered
    // by `getTextEditorExtenstions` for exactly that list, and acting on
    // the wrong belief is what sent X down the plain-text path.
    //
    // Inserting plain `@handle` text leaves the caret inside an ordinary
    // text run, and tiptap's suggestion plugin recomputes its match from
    // `$position.nodeBefore` on EVERY transaction -- it cannot tell text
    // we just inserted from text the user is typing. With
    // `allowSpaces: true` and a 3-word bound, `@Kontentino` reads as a
    // live one-word query, so the inline typeahead immediately reopened
    // underneath the mention that had just been resolved. A trailing
    // space does not help: `isMentionQueryWithinBound` deliberately
    // ignores one so the popup survives the keystroke between "Coca" and
    // "Cola".
    //
    // Inserting the NODE is what the typeahead itself already does, and
    // it closes the popup for a structural reason rather than a
    // heuristic: `nodeBefore` is then the mention node, not a text node,
    // so `findSuggestionMatch` returns null before any query is built.
    // The stored text is unchanged either way -- `renderMentionText`'s
    // default branch serialises X's node back to exactly `@${label}`.
    //
    // Instagram keeps the plain-text path, because it genuinely has no
    // node (absent from `PAGE_MENTION_PAGE_TYPES`), so a `mention` span
    // would have no parse rule to land on and would publish as literal
    // markup. It also has no typeahead, so nothing can reopen there.
    const hasMentionNode = PAGE_MENTION_PAGE_TYPES.includes(props.pageType);

    props.onCopyToText(
      hasMentionNode
        ? TextEditorConvertor.suggestionToMentionTag({
            id: item.id,
            name: item.name,
            label,
            // `renderMentionText` branches LinkedIn on this: `user`
            // becomes `urn:li:person:<id>`, anything else
            // `company:<id>`. Passed explicitly rather than left to
            // chance — resolve used to return companies only (spec D3),
            // so omitting it happened to serialise correctly, but that
            // was a property of the scope rather than of this call site,
            // and api#1060 widened the scope.
            role: item.role,
            link: item.link ?? undefined,
          })
        : `@${label}`,
    );

    SuggestionApi.reportSelection({
      pageId: props.pageId,
      entityKind: item.kind,
      entityId: item.id,
      entityName: item.name,
      source: 'resolve',
    });
  }

  const findMention = useMutation(
    (query: string) =>
      SuggestionApi.resolveMention({ pageId: props.pageId, query }),
    {
      // Every outcome is cleared before the next submit, so a stale notice
      // never sits under a fresh answer.
      onMutate: () => {
        setResult(null);
        setChoices(null);
      },
      onSuccess(envelope) {
        if (envelope.items.length > 1) {
          // Ambiguous: let the user pick. See `choices` above for why this is
          // not "insert the first one".
          setChoices(envelope.items);

          return;
        }

        const [item] = envelope.items;

        if (item) {
          insertCandidate(item);

          return;
        }

        // Everything that is not a candidate stays INLINE rather than
        // becoming a toast: the user is mid-edit in this field, and a notice
        // that disappears on a timer is the wrong shape for "here is what to
        // do instead".
        setResult(envelope);
      },
      onError: () => {
        toast(t('mentions.search.transportError'), 'warning');
      },
    },
  );

  const notice = (() => {
    if (!result) {
      return null;
    }

    if (result.failure) {
      return (
        <MentionSearchFailureNotice
          variant="failure"
          failure={result.failure}
          pageType={props.pageType}
        />
      );
    }

    // An unrecognised code must NOT fall through to the no-match copy. The
    // mapper already assumes server and client builds can differ, so a refusal
    // reason added server-side would otherwise render as "no page matches
    // that name or link" — the exact misleading answer this flow replaces.
    const copyKey = result.refusal
      ? REFUSAL_COPY_KEY[result.refusal] ?? 'mentions.resolve.refused'
      : null;

    return (
      <div
        className="tw-mt-3 tw-rounded-lg tw-bg-grayscale-5 tw-p-3 tw-text-md tw-text-grayscale-180"
        data-cy="direct-mention_notice"
        data-name="direct-mention_notice"
      >
        {copyKey ? t(copyKey) : t('mentions.resolve.empty')}
      </div>
    );
  })();

  return (
    <div className="tw-h-auto tw-rounded-md tw-border tw-border-solid tw-border-grayscale-20 tw-bg-white tw-p-4">
      <div className="tw-mb-4 tw-flex tw-items-center tw-gap-1">
        <span className="tw-text-lg tw-font-semibold">
          {t('mentions.modalTitle')}
        </span>
      </div>
      <form
        onSubmit={preventDefault(() => findMention.mutate(value))}
        className="tw-flex tw-flex-col tw-gap-4 tw-px-1"
      >
        <Field.Group>
          <Field.Label required>{t('mentions.nameSlugOrUrl')}</Field.Label>
          <TextInput
            placeholder={t('mentions.placeholder')}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            data-cy="direct-mention_input"
            data-name="direct-mention_input"
          />
        </Field.Group>
        <Field.Group>
          <Button
            type="submit"
            className="tw-mb-1 tw-ml-auto"
            isLoading={findMention.isLoading}
            disabled={value.trim() === ''}
            data-cy="direct-mention_submit"
            data-name="direct-mention_submit"
          >
            {t('add')}
          </Button>
        </Field.Group>
      </form>
      {choices && (
        <div
          className="tw-mt-3"
          data-cy="direct-mention_choices"
          data-name="direct-mention_choices"
        >
          <div className="tw-mb-2 tw-text-md tw-text-grayscale-180">
            {t('mentions.resolve.chooseOne')}
          </div>
          {/*
           * The same list the inline typeahead renders, deliberately: it
           * already applies the handle-as-label rule per network and shows the
           * avatar, verification badge and subtitle that are the only things
           * telling two same-named companies apart.
           *
           * The insertion runs from `onSelect`, not from `command`, because
           * `command`'s payload carries only id/label/role/link — and
           * `insertCandidate` needs `kind`, `name` and `handle` too, for the
           * label rule and for `reportSelection`. `command` is required by the
           * list's contract, so it is present and does nothing here.
           */}
          <SuggestionsList
            items={choices}
            pageType={props.pageType}
            command={() => {}}
            onSelect={(item) => {
              setChoices(null);
              insertCandidate(item as MentionItem);
            }}
          />
        </div>
      )}
      {notice}
    </div>
  );
};

export default DirectMention;
